import { useEffect, useState, useCallback } from "react";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, CheckCircle, XCircle, Trash, Airplane, ClockCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

const LEAVE_TYPES = [
  { value: "casual", label: "Casual" },
  { value: "sick", label: "Sick" },
  { value: "earned", label: "Earned" },
];
const STATUS_TONE = {
  pending: "text-[#B45309] border-[#F59E0B] bg-amber-50",
  approved: "text-[#16A34A] border-[#16A34A] bg-green-50",
  rejected: "text-[#FF2400] border-[#FF2400] bg-red-50",
};

const fmtDate = (s) => (s ? new Date(s).toLocaleString() : "—");

export default function Leaves() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isHr = isAdmin || user?.hr_access === true;
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [filterStatus, setFilterStatus] = useState("all");
  const [applyOpen, setApplyOpen] = useState(false);
  const [form, setForm] = useState({
    leave_type: "casual",
    start_date: "",
    end_date: "",
    reason: "",
    is_half_day: false,
    half_day: "morning",
  });
  const [hrOpen, setHrOpen] = useState(false);
  const [hrForm, setHrForm] = useState({
    user_id: "",
    leave_type: "casual",
    start_date: "",
    end_date: "",
    reason: "",
    auto_approve: true,
  });
  const [decideOpen, setDecideOpen] = useState(false);
  const [decision, setDecision] = useState(null);
  const [adminNote, setAdminNote] = useState("");

  const load = useCallback(async () => {
    const params = {};
    if (filterStatus !== "all") params.status = filterStatus;
    const { data } = await api.get("/leaves", { params });
    setRows(data);
  }, [filterStatus]);

  useEffect(() => {
    load();
    if (isHr) {
      api.get("/users").then((r) => setUsers(r.data.filter((u) => u.active))).catch(() => {});
    }
  }, [load, isHr]);

  const submitLeave = async () => {
    if (!form.start_date || !form.end_date || !form.reason.trim()) {
      toast.error("Fill start, end and reason");
      return;
    }
    if (form.end_date < form.start_date) {
      toast.error("End date must be after start date");
      return;
    }
    if (form.is_half_day && form.start_date !== form.end_date) {
      toast.error("Half-day leave must be a single day");
      return;
    }
    try {
      await api.post("/leaves", {
        leave_type: form.leave_type,
        start_date: form.start_date,
        end_date: form.end_date,
        reason: form.reason,
        half_day: form.is_half_day ? form.half_day : null,
      });
      toast.success("Leave request submitted");
      setApplyOpen(false);
      setForm({ leave_type: "casual", start_date: "", end_date: "", reason: "", is_half_day: false, half_day: "morning" });
      load();
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const cancelLeave = async (lv) => {
    if (!window.confirm("Cancel this leave request?")) return;
    try {
      await api.delete(`/leaves/${lv.id}`);
      toast.success("Cancelled");
      load();
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const openDecide = (lv, action) => {
    setDecision({ leave: lv, action });
    setAdminNote("");
    setDecideOpen(true);
  };

  const submitDecision = async () => {
    if (!decision) return;
    try {
      await api.post(`/leaves/${decision.leave.id}/decide`, {
        decision: decision.action,
        admin_note: adminNote || null,
      });
      toast.success(`Leave ${decision.action}`);
      setDecideOpen(false);
      setDecision(null);
      load();
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const submitHrLeave = async () => {
    if (!hrForm.user_id || !hrForm.start_date || !hrForm.end_date || !hrForm.reason.trim()) {
      toast.error("Fill employee, dates and reason");
      return;
    }
    if (hrForm.end_date < hrForm.start_date) {
      toast.error("End date must be after start date");
      return;
    }
    try {
      await api.post("/hr/leaves", hrForm);
      toast.success(`Leave ${hrForm.auto_approve ? "approved" : "created (pending)"}`);
      setHrOpen(false);
      setHrForm({ user_id: "", leave_type: "casual", start_date: "", end_date: "", reason: "", auto_approve: true });
      load();
    } catch (e) { toast.error(errorMessage(e)); }
  };

  return (
    <div className="space-y-6" data-testid="leaves-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-gray-500 font-bold">Time Off</p>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight">
            {isHr ? "Team Leave Requests" : "My Leaves"}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {isHr ? "Approve, reject, or create leaves on behalf of any employee." : "Apply for leave and track approval status."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="h-9 w-40" data-testid="leaves-filter-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
          {isHr && (
            <Dialog open={hrOpen} onOpenChange={setHrOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#0047AB] hover:bg-[#0033A0] text-white rounded-sm h-9" data-testid="hr-add-leave-button">
                  <Plus size={14} weight="bold" className="mr-1" /> Add leave for employee
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-sm">
                <DialogHeader>
                  <DialogTitle className="font-display tracking-tight flex items-center gap-2">
                    <Airplane size={18} weight="duotone" className="text-[#0047AB]" /> Add Leave (HR)
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label>Employee</Label>
                    <Select value={hrForm.user_id} onValueChange={(v) => setHrForm({ ...hrForm, user_id: v })}>
                      <SelectTrigger data-testid="hr-leave-user-select">
                        <SelectValue placeholder="Select employee" />
                      </SelectTrigger>
                      <SelectContent>
                        {users.map((u) => (
                          <SelectItem key={u.id} value={u.id}>{u.name} · {u.role}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Leave type</Label>
                    <Select value={hrForm.leave_type} onValueChange={(v) => setHrForm({ ...hrForm, leave_type: v })}>
                      <SelectTrigger data-testid="hr-leave-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {LEAVE_TYPES.map((t) => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Start date</Label>
                      <Input type="date" value={hrForm.start_date} onChange={(e) => setHrForm({ ...hrForm, start_date: e.target.value })} data-testid="hr-leave-start-date" />
                    </div>
                    <div className="space-y-2">
                      <Label>End date</Label>
                      <Input type="date" value={hrForm.end_date} onChange={(e) => setHrForm({ ...hrForm, end_date: e.target.value })} data-testid="hr-leave-end-date" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Reason</Label>
                    <Textarea rows={3} value={hrForm.reason} onChange={(e) => setHrForm({ ...hrForm, reason: e.target.value })} placeholder="e.g. Family emergency, medical, etc." data-testid="hr-leave-reason" />
                  </div>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={hrForm.auto_approve}
                      onChange={(e) => setHrForm({ ...hrForm, auto_approve: e.target.checked })}
                      data-testid="hr-leave-auto-approve"
                    />
                    <span>Auto-approve (skip pending state)</span>
                  </label>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setHrOpen(false)} className="rounded-sm">Cancel</Button>
                  <Button onClick={submitHrLeave} className="bg-[#0047AB] hover:bg-[#0033A0] text-white rounded-sm" data-testid="hr-leave-submit-button">
                    Add leave
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          {!isHr && (
            <Dialog open={applyOpen} onOpenChange={setApplyOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#0047AB] hover:bg-[#0033A0] text-white rounded-sm h-9" data-testid="apply-leave-button">
                  <Plus size={14} weight="bold" className="mr-1" /> Apply leave
                </Button>
              </DialogTrigger>              <DialogContent className="rounded-sm">
                <DialogHeader>
                  <DialogTitle className="font-display tracking-tight flex items-center gap-2">
                    <Airplane size={18} weight="duotone" className="text-[#0047AB]" /> Apply for Leave
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label>Leave type</Label>
                    <Select value={form.leave_type} onValueChange={(v) => setForm({ ...form, leave_type: v })}>
                      <SelectTrigger data-testid="leave-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {LEAVE_TYPES.map((t) => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Start date</Label>
                      <Input
                        type="date"
                        value={form.start_date}
                        onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                        data-testid="leave-start-date"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>End date</Label>
                      <Input
                        type="date"
                        value={form.end_date}
                        onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                        data-testid="leave-end-date"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Reason</Label>
                    <Textarea
                      rows={3}
                      value={form.reason}
                      onChange={(e) => setForm({ ...form, reason: e.target.value })}
                      placeholder="Family function, health check-up, etc."
                      data-testid="leave-reason"
                    />
                  </div>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.is_half_day}
                      onChange={(e) => setForm({ ...form, is_half_day: e.target.checked, end_date: e.target.checked ? form.start_date : form.end_date })}
                      data-testid="leave-half-day-checkbox"
                    />
                    <span>Half day (counts as 0.5)</span>
                  </label>
                  {form.is_half_day && (
                    <div className="space-y-2">
                      <Label>Which half?</Label>
                      <Select value={form.half_day} onValueChange={(v) => setForm({ ...form, half_day: v })}>
                        <SelectTrigger data-testid="leave-half-day-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="morning">Morning</SelectItem>
                          <SelectItem value="afternoon">Afternoon</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setApplyOpen(false)} className="rounded-sm">Cancel</Button>
                  <Button onClick={submitLeave} className="bg-[#0047AB] hover:bg-[#0033A0] text-white rounded-sm" data-testid="leave-submit-button">
                    Submit request
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      <div className="border border-gray-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50/60">
                {isHr && <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-bold text-gray-500">Employee</th>}
                <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-bold text-gray-500">Type</th>
                <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-bold text-gray-500">Dates</th>
                <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-bold text-gray-500">Days</th>
                <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-bold text-gray-500">Reason</th>
                <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-bold text-gray-500">Status</th>
                <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-bold text-gray-500">Applied</th>
                <th className="text-right px-4 py-2.5 text-[11px] uppercase tracking-wider font-bold text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="leaves-rows">
              {rows.length === 0 && (
                <tr><td colSpan={isHr ? 8 : 7} className="px-4 py-10 text-center text-gray-500 text-sm">
                  No leave requests {filterStatus !== "all" ? `with status "${filterStatus}"` : "yet"}.
                </td></tr>
              )}
              {rows.map((lv) => (
                <tr key={lv.id} className="border-b border-gray-100 hover:bg-gray-50 align-top">
                  {isHr && <td className="px-4 py-3 font-medium">{lv.user_name}</td>}
                  <td className="px-4 py-3 uppercase text-xs font-bold tracking-wider">{lv.leave_type}</td>
                  <td className="px-4 py-3 font-mono text-xs">{lv.start_date} → {lv.end_date}{lv.half_day ? ` · ½ ${lv.half_day}` : ""}</td>
                  <td className="px-4 py-3 font-mono text-xs">{Number(lv.days_count).toFixed(lv.half_day ? 1 : 0)}</td>
                  <td className="px-4 py-3 text-gray-700 max-w-[280px]">
                    <p className="line-clamp-2">{lv.reason}</p>
                    {lv.admin_note && (
                      <p className="text-[11px] text-gray-500 mt-1 italic">
                        Note: {lv.admin_note}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 border rounded-sm ${STATUS_TONE[lv.status]}`}>
                      {lv.status}
                    </span>
                    {lv.decided_by_name && (
                      <p className="text-[10px] text-gray-500 mt-1">by {lv.decided_by_name}</p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500 flex items-center gap-1">
                    <ClockCounterClockwise size={12} />{fmtDate(lv.created_at)}
                  </td>
                  <td className="px-4 py-3 text-right space-x-1 whitespace-nowrap">
                    {isHr && lv.status === "pending" && (
                      <>
                        <Button
                          size="sm"
                          className="h-8 bg-[#16A34A] hover:bg-[#15803D] text-white rounded-sm"
                          onClick={() => openDecide(lv, "approved")}
                          data-testid={`leave-approve-${lv.id}`}
                        >
                          <CheckCircle size={12} weight="bold" className="mr-1" /> Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 border-[#FF2400] text-[#FF2400] hover:bg-red-50 rounded-sm"
                          onClick={() => openDecide(lv, "rejected")}
                          data-testid={`leave-reject-${lv.id}`}
                        >
                          <XCircle size={12} weight="bold" className="mr-1" /> Reject
                        </Button>
                      </>
                    )}
                    {!isHr && lv.status === "pending" && (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-[#FF2400]"
                        onClick={() => cancelLeave(lv)}
                        title="Cancel request"
                        data-testid={`leave-cancel-${lv.id}`}
                      >
                        <Trash size={12} />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={decideOpen} onOpenChange={setDecideOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              {decision?.action === "approved" ? "Approve leave request" : "Reject leave request"}
            </DialogTitle>
          </DialogHeader>
          {decision && (
            <div className="space-y-3">
              <p className="text-sm text-gray-700">
                <b>{decision.leave.user_name}</b> is requesting <b>{decision.leave.days_count}</b> day
                {decision.leave.days_count > 1 ? "s" : ""} ({decision.leave.leave_type}) from{" "}
                <span className="font-mono">{decision.leave.start_date}</span> to{" "}
                <span className="font-mono">{decision.leave.end_date}</span>.
              </p>
              <p className="text-sm text-gray-600 italic border-l-2 border-gray-200 pl-3">
                {decision.leave.reason}
              </p>
              <div className="space-y-2">
                <Label>Note to agent (optional)</Label>
                <Textarea
                  rows={2}
                  value={adminNote}
                  onChange={(e) => setAdminNote(e.target.value)}
                  placeholder="e.g. Approved. Handover pending tickets before you leave."
                  data-testid="leave-admin-note"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecideOpen(false)} className="rounded-sm">Cancel</Button>
            <Button
              onClick={submitDecision}
              className={`rounded-sm text-white ${decision?.action === "approved" ? "bg-[#16A34A] hover:bg-[#15803D]" : "bg-[#FF2400] hover:bg-[#DC2626]"}`}
              data-testid="leave-decide-submit"
            >
              Confirm {decision?.action === "approved" ? "approve" : "reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
